import { Fragment, type ReactNode } from "react";

/**
 * Tiny, dependency-free Markdown renderer for the guarded model's reply.
 * Handles headings, bold, inline code, and unordered/ordered lists — enough to make a
 * typical chat completion read cleanly without pulling in a full parser or using innerHTML.
 */
function renderInline(text: string, keyBase: string): ReactNode[] {
  // Split on **bold** and `code`, keeping delimiters.
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={`${keyBase}-${i}`} className="font-semibold text-ink">
          {p.slice(2, -2)}
        </strong>
      );
    }
    if (p.startsWith("`") && p.endsWith("`")) {
      return (
        <code key={`${keyBase}-${i}`} className="mono rounded bg-white/10 px-1 py-0.5 text-[0.9em]">
          {p.slice(1, -1)}
        </code>
      );
    }
    return <Fragment key={`${keyBase}-${i}`}>{p}</Fragment>;
  });
}

export function Markdown({ text }: { text: string }) {
  const lines = text.split("\n");
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushList = (key: string) => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    blocks.push(
      <Tag
        key={key}
        className={`my-1.5 ml-5 flex flex-col gap-1 ${list.ordered ? "list-decimal" : "list-disc"}`}
      >
        {list.items.map((it, i) => (
          <li key={i} className="pl-1 marker:text-ink-faint">
            {renderInline(it, `${key}-${i}`)}
          </li>
        ))}
      </Tag>,
    );
    list = null;
  };

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    const ordered = /^\s*\d+\.\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);

    if (heading) {
      flushList(`l${idx}`);
      blocks.push(
        <div key={idx} className="mt-2 mb-1 text-[14px] font-bold text-ink first:mt-0">
          {renderInline(heading[1]!, `h${idx}`)}
        </div>,
      );
    } else if (ordered) {
      if (!list || !list.ordered) {
        flushList(`l${idx}`);
        list = { ordered: true, items: [] };
      }
      list.items.push(ordered[1]!);
    } else if (bullet) {
      if (!list || list.ordered) {
        flushList(`l${idx}`);
        list = { ordered: false, items: [] };
      }
      list.items.push(bullet[1]!);
    } else if (line.trim() === "") {
      flushList(`l${idx}`);
    } else {
      flushList(`l${idx}`);
      blocks.push(
        <p key={idx} className="my-1 first:mt-0">
          {renderInline(line, `p${idx}`)}
        </p>,
      );
    }
  });
  flushList("l-end");

  return <div className="text-[13.5px] leading-relaxed text-ink/90">{blocks}</div>;
}
